import React, { useEffect } from "react";

import {
  Autocomplete,
  Box,
  TextField,
  Alert,
  Typography,
  Paper,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import FolderIcon from "@mui/icons-material/Folder";

import { fetchProjects } from "../../../Dashboard/services/projects.service";

interface Props {
  handleOnChange: (event: React.SyntheticEvent, data: any) => void;
  hasError?: boolean;
  value?: { label: string; id: string } | null;
}

export default function SelectProject({
  handleOnChange,
  hasError = false,
  value,
}: Props) {
  const {
    data: response,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["projects"],
    queryFn: () => fetchProjects(),
  });

  const options =
    response?.projects?.map((project: any) => {
      return { label: project.name, id: project.id };
    }) || [];

  // Auto-select the project if there's only one available
  useEffect(() => {
    if (options.length === 1 && !value) {
      const singleProject = options[0];
      // Simulate the change event to auto-select
      handleOnChange({} as React.SyntheticEvent, singleProject);
    }
  }, [options, value, handleOnChange]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 500,
        mx: "auto",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <FolderIcon
            sx={{
              fontSize: 48,
              color: "#1976d2",
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              mb: 1,
              color: "#23272E",
              fontWeight: 600,
            }}
          >
            Select Project
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#666",
            }}
          >
            Choose which project this episode belongs to
          </Typography>
        </Box>

        <Autocomplete
          disablePortal
          clearOnEscape
          options={options || []}
          loading={isLoading}
          disabled={isLoading || !!error}
          value={value}
          onOpen={() => {
            // Refetch projects when dropdown is opened to get latest data
            refetch();
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              fontSize: 16,
              backgroundColor: "#fff",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: 16,
              fontWeight: 500,
              color: "#666",
              "&.Mui-focused": {
                color: "#1976d2",
                fontWeight: 600,
              },
            },
            "& .MuiAutocomplete-option": {
              fontSize: 15,
              paddingY: 1.5,
              paddingX: 2,
              borderRadius: 2,
              transition: "background 0.15s",
              '&[aria-selected="true"]': {
                background: "#e3f2fd",
                color: "#1976d2",
              },
              "&:hover": {
                background: "#f5f7fa",
              },
            },
            "& .MuiAutocomplete-listbox": {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
              p: 0.5,
              background: "#fff",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Select Project"
              placeholder={
                isLoading ? "Loading projects..." : "Choose a project..."
              }
            />
          )}
          onChange={handleOnChange}
          isOptionEqualToValue={(option, selectedValue) =>
            option.id === selectedValue.id
          }
        />

        {error && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 3,
                "& .MuiAlert-message": {
                  fontSize: 14,
                  fontWeight: 500,
                },
              }}
            >
              Failed to load projects. Please try again.
            </Alert>
          </Box>
        )}

        {hasError && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 3,
                "& .MuiAlert-message": {
                  fontSize: 14,
                  fontWeight: 500,
                },
              }}
            >
              Please select a project before uploading the episode.
            </Alert>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
